import {
	ComplexCommissionTier,
	DefaultInput,
	GrowthBonusTier,
	MetricTierItem,
	OrdinalTier,
} from "@PayPlans/helpers/DefaultSections/Commons";

const isCombinedFranchises = (franchises = []) => franchises?.length > 1;

const defaultCommissionsDefinition = (make, tiers, optionalArgs) => {
	const { applyTiersRetroactively = false } = optionalArgs ?? {};
	return {
		make,
		applyTiersRetroactively,
		tiers: tiers ?? [],
	};
};

const defaultCommissionTier = (from = 0, to = 0, definition = { commission: 0, input: "Percent" }) => ({
	from: DefaultInput(from, "Unit"),
	to: DefaultInput(to, "Unit"),
	commission: DefaultInput(definition.commission, definition.input),
});

const defaultCommissionsTier = (from = 1, to = 100, commissions = []) => ({
	from: DefaultInput(from, "Dollar"),
	to: DefaultInput(to, "Dollar"),
	commissions,
});

const defaultManagerCommission = (franchises = []) => {
	const combineFranchises = isCombinedFranchises(franchises);
	let newVehicles = {};

	const max = 99999.99;
	const maxTier = 9999;
	const tierDefault = defaultCommissionsTier(-max, max, [defaultCommissionTier(0, maxTier, { commission: 0, input: "Percent" })]);

	if (combineFranchises) {
		newVehicles = {
			makes: [defaultCommissionsDefinition("newVehicles", [{ ...tierDefault }], { applyTiersRetroactively: false })],
			variableGrossStructure: franchises?.map((franchise) => {
				return { make: franchise, checked: false };
			}),
		};
	} else {
		newVehicles = {
			makes: [
				defaultCommissionsDefinition(franchises?.[0] ?? "newVehicles", [{ ...tierDefault }], {
					applyTiersRetroactively: false,
				}),
			],
		};
	}

	const preOwned = defaultCommissionsDefinition("preOwned", [{ ...tierDefault }], { applyTiersRetroactively: false });

	const finance = {
		makes: [],
		variableGrossStructure: { newFinance: false, preOwnedFinance: false },
		combinedOEMsGrossStructure: null,
		newAndPreOwnedRequired: false,
		globalUnitCounts: false,
		combineOEMs: false,
	};

	return {
		newVehicles,
		preOwned,
		finance,
		combineFranchises,
		newAndPreOwnedRequired: false,
		globalUnitCounts: false,
		isGuaranteedDraw: false,
	};
};

const grossBonus = (definition, section = "bodyShop", args = { frequency: "MoM", applyTiersRetroactively: false }) => {
	const { frequency, applyTiersRetroactively } = args;
	const maxCommission = 9999999;
	const maxGrowth = 100;
	return {
		...definition,
		grossOptions: {
			bodyShop: Boolean(section === "bodyShop"),
			parts: Boolean(section === "parts"),
			service: Boolean(section === "service"),
		},
		frequency,
		tiers: [
			ComplexCommissionTier([0, "Dollar"], [maxCommission, "Dollar"], {
				identifier: "bonuses",
				commissions: [GrowthBonusTier(-maxGrowth, maxGrowth)],
			}),
		],
		applyTiersRetroactively,
	};
};

// References can be: "BODY_SHOP", "PARTS" and "SERVICE", or a combination of all of these.
const defaultGrossDepartmental = (locationOEMs = [], section = "bodyShop") => {
	if (!locationOEMs?.length) {
		return null;
	}

	let makes = locationOEMs.map((oem) => ({ identifier: oem.oemDisplayName, make: oem.oemName, dealerCodeFS: oem.dealerCodeFS }));
	let make = "New Vehicles";

	switch (section) {
		case "bodyShop":
			make = "Body Shop";
			break;
		case "parts":
			make = "Parts";
			break;
		case "service":
			make = "Mechanical";
			break;
		default:
			break;
	}

	// Combining when only one oem is specified
	const combined = makes.length === 1;
	if (combined) {
		makes = [{ make, identifier: make, dealerCodeFS: "" }];
	}

	return {
		makes: makes.map((definition) => grossBonus(definition, section)),
		combineOEMs: combined,
		isGuaranteedDraw: false,
	};
};

const getGrossReferenceTitle = (reference) => {
	if (!reference) {
		return "";
	}

	let title = "";
	switch (reference) {
		case "bodyShop":
			title = "Body Shop";
			break;
		case "parts":
			title = "Parts";
			break;
		case "service":
			title = "Service";
			break;
		default:
			title = "Unknown";
			break;
	}
	return `Total ${title} Gross`;
};

const growthCommonProperties = (args) => {
	const { frequency = "MoM", applyTiersRetroactively = false, isRetail = true, isFinance = false } = args || {};
	return {
		frequency,
		applyTiersRetroactively,
		isRetail,
		isFinance,
	};
};

const growthBonusDefinition = (make, tiers, args) => {
	const { isFleet = false } = args || {};
	const properties = growthCommonProperties(args);
	return {
		...properties,
		make,
		isFleet,
		tiers: tiers || [],
	};
};

const growthBonusDefinitionPreOwned = (make, tiers, args) => {
	const { isWholesale = false } = args || {};
	const properties = growthCommonProperties(args);

	return {
		...properties,
		make,
		isWholesale,
		tiers: tiers || [],
	};
};

const growthBonusDefault = (franchises = []) => {
	const min = -100;
	const max = 10000;

	const makes = franchises.map((franchise) => {
		const growthTier = ComplexCommissionTier([min, "Percent"], [max, "Percent"], {
			identifier: "bonuses",
			commissions: [GrowthBonusTier(min, max)],
		});
		return growthBonusDefinition(franchise, [{ ...growthTier }], { frequency: "MoM" });
	});

	return {
		makes,
		franchises,
		newAndPreOwnedRequired: false,
		combineFranchises: false,
		preOwned: growthBonusDefinitionPreOwned(
			"Pre-Owned Vehicles",
			[
				ComplexCommissionTier([min, "Percent"], [max, "Percent"], {
					identifier: "bonuses",
					commissions: [GrowthBonusTier(min, max)],
				}),
			],
			{ frequency: "MoM" }
		),
	};
};

const operatingCostsMakesDefinition = (make, dealerCodeFS, args) => {
	const { tiers = [], applyTiersRetroactively = false, frequency = "MoM" } = args || {};

	return {
		make,
		frequency,
		tiers,
		applyTiersRetroactively,
		dealerCodeFS: dealerCodeFS || "",
	};
};

const operatingCostsCommissionTier = (from = 100, to = -100, definition = { commission: 0, input: "Percent" }) => ({
	from: DefaultInput(from, "Percent"),
	to: DefaultInput(to, "Percent"),
	commission: DefaultInput(definition?.commission || 0, definition?.input || 0),
});

const operatingCostsDefault = (OEMs = []) => {
	const max = 500000000;
	const minCost = 10000;
	const maxCost = 100;

	const oemsCommissions = OEMs.map((oem) =>
		operatingCostsMakesDefinition(oem?.oemDisplayName ?? oem.oemName, oem.dealerCodeFS, {
			tiers: [defaultCommissionsTier(max, 0, [operatingCostsCommissionTier(minCost, -maxCost, { commission: 0, input: "Percent" })])],
		})
	);

	return {
		combineOEMs: false,
		makes: oemsCommissions,
		OEMs,
	};
};

const pinnacleDefault = (metrics = [], numberRequired = 0, oemReference = "GM") => ({
	metrics,
	oemReference,
	version: "",
	tiers: [MetricTierItem(DefaultInput(0, "Percent"), DefaultInput(0, "Percent"), numberRequired)],
});

const rankingBonusesTier = (from = 1, to = 10000, bonuses = []) => ({
	from: DefaultInput(from, "Ordinal"),
	to: DefaultInput(to, "Ordinal"),
	bonuses,
});

const rankingBonusDefault = (franchises = []) => {
	const maxRank = 15000;

	const makes = franchises.map((make) => ({
		make,
		tiers: [
			rankingBonusesTier(1, maxRank, [
				OrdinalTier({
					from: 1,
					to: maxRank,
					bonus: 0,
					bonusInput: "Percent",
				}),
			]),
		],
	}));

	return {
		makes,
		simultaneousRanking: false,
	};
};

const excellence = (make, args) => {
	const { potentialPayout = "From Financial Statement", numberRequired = 0, metrics = [], oemReference = "", version = "" } = args || {};
	return {
		make,
		oemReference,
		version,
		metrics,
		potentialPayout,
		manualInput: DefaultInput(0, "Dollar"),
		tiers: [MetricTierItem(DefaultInput(0, "Percent"), DefaultInput(0, "Percent"), numberRequired)],
	};
};

const excellenceMakes = (makes = [], includesMakes = ["Chevrolet", "Buick", "GMC"]) => {
	const filteredMakes = makes.filter((make) => includesMakes.includes(make.franchise));
	const makeMetrics = filteredMakes?.map((make) => excellence(make.franchise, { oemReference: make?.oemReference ?? "" }));
	return { makeMetrics };
};

const getCrossCommissionLabel = (type) => {
	const crossStoreLabels = new Map([
		["newVehicles", "New Vehicles"],
		["preOwned", "Pre-Owned Vehicles"],
		["finance", "F&I"],
	]);
	return crossStoreLabels.get(type) || "";
};

const crossCommissions = (type = "", tiers = [], optionalArgs = {}) => {
	const { applyRetroactively = false, individual = false } = optionalArgs ?? {};
	return {
		tiers,
		individual,
		make: getCrossCommissionLabel(type),
		applyTiersRetroactively: applyRetroactively,
	};
};

const crossVariableGrossStructure = (type) => {
	const options =
		type === "finance" ? ["New F&I Inbound", "Used F&I Inbound", "New F&I Outbound", "Used F&I Outbound"] : ["Inbound", "Outbound"];
	const selected = (option = "") => option.includes("Outbound");
	return options.map((option) => ({ make: option, checked: selected(option) }));
};

const crossStoreCommission = (type = "newVehicles", hasUnitCounts = false, globalUnitCounts = false) => {
	const max = 99999.99;
	const maxTier = 9999;
	const tierDefault = defaultCommissionsTier(-max, max, [defaultCommissionTier(0, maxTier, { commission: 0, input: "Percent" })]);

	const crossDefinition = crossCommissions(type, [{ ...tierDefault }]);
	const commissions = type === "finance" ? [crossDefinition] : crossDefinition;

	const crossStore = {
		commissions,
		variableGrossStructure: [...crossVariableGrossStructure(type)],
	};

	if (type !== "finance") {
		crossStore["miniDeal"] = [];
	}
	if (hasUnitCounts) {
		crossStore["globalUnitCounts"] = globalUnitCounts;
	}
	return crossStore;
};

const crossStoreCommissions = () => ({
	newVehicles: crossStoreCommission("newVehicles"),
	preOwned: crossStoreCommission("preOwned"),
	finance: crossStoreCommission("finance", true, true),
	globalUnitCounts: false,
});

export {
	crossStoreCommissions as CrossStoreCommissions,
	crossVariableGrossStructure as CrossVariableGrossStructure,
	defaultManagerCommission as DefaultManagerCommission,
	defaultCommissionsDefinition as DefaultCommissionsDefinition,
	defaultCommissionTier as DefaultCommissionTier,
	defaultCommissionsTier as DefaultCommissionsTier,
	defaultGrossDepartmental as DefaultGrossDepartmental,
	excellenceMakes as ExcellenceMakesDefault,
	grossBonus as GrossBonus,
	growthBonusDefault as GrowthBonusDefault,
	growthBonusDefinition as GrowthBonusDefinition,
	operatingCostsDefault as OperatingCostsDefault,
	operatingCostsCommissionTier as OperatingCostsCommissionTier,
	operatingCostsMakesDefinition as OperatingCostsMakesDefinition,
	pinnacleDefault as PinnacleDefault,
	rankingBonusDefault as RankingBonusDefault,
	rankingBonusesTier as RankingBonusesTier,
	getGrossReferenceTitle,
};
